import React, { useEffect, useState } from 'react';
import { getCustomerFollowUps, addFollowUp, updateFollowUp, deleteFollowUp } from '../services/followUpApi';

// FOLLOW-UP v1: "Log call" window for one customer.
export const FU_OUTCOMES = [
  ['will_order', 'Will order', 7],
  ['price', 'Price / rate issue', 14],
  ['later', 'Call later', 7],
  ['not_reachable', 'Not reachable', 2],
  ['lost', 'Lost / not interested', 60],
];
export const FU_SHORT = { will_order: 'Will order', price: 'Price issue', later: 'Call later', not_reachable: 'Not reachable', lost: 'Lost' };
const dmy = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—');
const FIELD = { padding: '6px 10px', border: '1px solid var(--color-border, #ccc)', borderRadius: 6, background: 'var(--color-surface, #fff)', color: 'inherit', width: '100%' };

export default function FollowUpModal({ customer, onClose, onSaved }) {
  const [outcome, setOutcome] = useState('will_order');
  const [note, setNote] = useState('');
  const [days, setDays] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [hist, setHist] = useState(null);
  // FOLLOW-EDIT v1: editId = the history row being edited (null = adding a new call); delId = row waiting for "Sure?"
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [changed, setChanged] = useState(false);
  const reload = () => getCustomerFollowUps(customer.id).then((x) => setHist(x)).catch(() => setHist([]));
  useEffect(() => {
    let off = false;
    getCustomerFollowUps(customer.id).then((x) => { if (!off) setHist(x); }).catch(() => { if (!off) setHist([]); });
    return () => { off = true; };
  }, [customer.id]);
  const errOf = (e) => (e && e.response && e.response.data && e.response.data.error && e.response.data.error.message) || 'Could not save';
  const startEdit = (h) => { setEditId(h.id); setDelId(null); setOutcome(h.outcome); setNote(h.note || ''); setDays(''); setErr(''); };
  const cancelEdit = () => { setEditId(null); setOutcome('will_order'); setNote(''); setDays(''); setErr(''); };
  const closeIt = () => { if (changed && onSaved) onSaved(); onClose(); };
  const doDelete = (h) => {
    setBusy(true); setErr('');
    deleteFollowUp(h.id)
      .then(() => { setChanged(true); setDelId(null); if (editId === h.id) cancelEdit(); return reload(); })
      .catch((e) => setErr(errOf(e)))
      .then(() => setBusy(false));
  };
  const defDays = (FU_OUTCOMES.find((o) => o[0] === outcome) || [0, 0, 7])[2];
  const save = () => {
    setBusy(true); setErr('');
    if (editId !== null) {
      updateFollowUp(editId, { outcome, note, snoozeDays: days === '' ? undefined : Number(days) })
        .then(() => { setChanged(true); cancelEdit(); return reload(); })
        .catch((e) => setErr(errOf(e)))
        .then(() => setBusy(false));
      return;
    }
    addFollowUp({ customerId: customer.id, outcome, note, snoozeDays: days === '' ? undefined : Number(days) })
      .then(() => { if (onSaved) onSaved(); onClose(); })
      .catch((e) => { setErr(errOf(e)); setBusy(false); });
  };
  return (
    <div role="dialog" aria-label="Log call" onClick={closeIt}
      style={{ position: 'fixed', inset: 0, zIndex: 3500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-surface, #fff)', color: 'var(--color-text-primary, #111)', border: '1px solid var(--color-border, #ccc)', borderRadius: 12, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <b>{editId !== null ? 'Edit call' : 'Log call'} — {customer.name}</b>
          <button type="button" onClick={closeIt} aria-label="Close" style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <label style={{ fontSize: 13 }}>What happened?
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} style={{ ...FIELD, marginTop: 4 }}>
            {FU_OUTCOMES.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>Note (optional)
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} placeholder="e.g. will order 600X600 next week" style={{ ...FIELD, marginTop: 4, resize: 'vertical' }} />
        </label>
        <label style={{ fontSize: 13 }}>Leave this customer alone in the daily Telegram list for (days)
          <input type="number" min="0" max="90" value={days} onChange={(e) => setDays(e.target.value)} placeholder={`${defDays} (default)`} style={{ ...FIELD, marginTop: 4 }} />
        </label>
        {err && <div style={{ color: '#B42318' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : (editId !== null ? 'Update' : 'Save')}</button>
          {editId !== null && <button type="button" className="btn" disabled={busy} onClick={cancelEdit}>Cancel edit</button>}
        </div>
        <div style={{ borderTop: '1px solid var(--color-border, #eee)', paddingTop: 8, fontSize: 13 }}>
          <b>Earlier calls</b>
          {hist === null && <div style={{ opacity: 0.7 }}>Loading…</div>}
          {hist && hist.length === 0 && <div style={{ opacity: 0.7 }}>No call logged yet.</div>}
          {hist && hist.map((h) => (
            <div key={h.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border, #eee)' }}>
              <b>{FU_SHORT[h.outcome] || h.outcome}</b> · {dmy(h.date)}{h.by ? ` · ${h.by}` : ''}
              {h.note ? <div style={{ opacity: 0.8 }}>{h.note}</div> : null}
              <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 12 }}>
                {delId === h.id ? (
                  <>
                    <span>Delete this entry?</span>
                    <button type="button" disabled={busy} onClick={() => doDelete(h)} style={{ border: 'none', background: 'transparent', color: '#B42318', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Yes, delete</button>
                    <button type="button" onClick={() => setDelId(null)} style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0 }}>No</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => startEdit(h)} style={{ border: 'none', background: 'transparent', color: 'var(--color-primary, #2563eb)', cursor: 'pointer', padding: 0 }}>Edit</button>
                    <button type="button" onClick={() => { setDelId(h.id); setErr(''); }} style={{ border: 'none', background: 'transparent', color: '#B42318', cursor: 'pointer', padding: 0 }}>Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
// FOLLOW-EDIT v1
